/**
 * Digest — weekly institutional-flow digest with browsable archive.
 */
import React, { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, Copy, Download, Loader2, Newspaper, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { API } from "../components/stats/statsApi";

const IssueListItem = ({ issue, active, onClick }) => (
  <button onClick={onClick} data-testid={`digest-issue-${issue.week_ending}`}
          className={`w-full text-left px-3 py-2.5 rounded-md border transition-colors ${
            active ? "border-primary bg-primary/10"
                   : "border-border hover:border-muted-foreground/40"}`}>
    <div className="text-sm font-semibold font-mono">{issue.week_ending}</div>
    <div className="text-[10px] text-muted-foreground mt-0.5">
      {issue.counts?.regime_upgrades ?? 0} upgrades · {issue.counts?.regime_downgrades ?? 0} downgrades
      · {issue.counts?.new_converged ?? 0} new · universe {issue.counts?.universe ?? "—"}
    </div>
  </button>
);

export default function DigestPage() {
  const { token } = useAuth();
  const [issues, setIssues] = useState([]);
  const [selected, setSelected] = useState(null);
  const [issue, setIssue] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [loadingIssue, setLoadingIssue] = useState(false);
  const [copied, setCopied] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadArchive = useCallback(async () => {
    const res = await fetch(`${API}/digest/archive`, auth);
    if (!res.ok) return;
    const body = await res.json();
    setIssues(body.issues || []);
    if (!selected && body.issues?.length) setSelected(body.issues[0].week_ending);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadIssue = useCallback(async (week) => {
    if (!week) return;
    setLoadingIssue(true);
    try {
      const res = await fetch(`${API}/digest/archive/${week}`, auth);
      if (res.ok) setIssue(await res.json());
    } finally { setLoadingIssue(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const publish = async () => {
    setPublishing(true);
    try {
      const res = await fetch(`${API}/digest/publish`, { method: "POST", ...auth });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "publish failed");
      toast.success(`Issue ${body.week_ending} published`);
      await loadArchive();
      setSelected(body.week_ending);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setPublishing(false); }
  };

  const copyForLLM = async () => {
    if (!issue?.markdown) return;
    await navigator.clipboard.writeText(issue.markdown);
    setCopied(true);
    toast.success("Markdown copied — paste into any LLM");
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadMd = () => {
    if (!issue?.markdown) return;
    const blob = new Blob([issue.markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Kensys_Weekly_Digest_${issue.week_ending}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => { if (token) loadArchive(); }, [token, loadArchive]);
  useEffect(() => { loadIssue(selected); }, [selected, loadIssue]);

  return (
    <div className="p-6 space-y-6 max-w-[1500px]" data-testid="digest-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Newspaper className="w-6 h-6 text-amber-400" /> Weekly Digest
          </h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Week-over-week deltas of the signal stack — new specialist convergence, regime
            upgrades &amp; downgrades, tape unlocks, Champions book changes and short-side
            qualifiers. Built for sourcing and triage.
          </p>
        </div>
        <Button onClick={publish} disabled={publishing} data-testid="digest-publish-btn">
          {publishing
            ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Generating…</>
            : <><RefreshCw className="w-4 h-4 mr-1" />Publish this week's issue</>}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className="space-y-2" data-testid="digest-archive-list">
          <div className="text-[11px] uppercase font-semibold text-muted-foreground">
            Archive ({issues.length})
          </div>
          {issues.length === 0 && (
            <p className="text-xs text-muted-foreground py-4">
              No issues yet — publish the first one.
            </p>
          )}
          {issues.map((i) => (
            <IssueListItem key={i.week_ending} issue={i}
                           active={i.week_ending === selected}
                           onClick={() => setSelected(i.week_ending)} />
          ))}
        </div>

        <Card className="lg:col-span-3">
          <CardContent className="p-6">
            {loadingIssue && (
              <div className="py-16 text-center text-muted-foreground">
                <Loader2 className="w-6 h-6 mx-auto animate-spin" />
              </div>
            )}
            {!loadingIssue && issue && (
              <div data-testid="digest-issue-view">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4 pb-3 border-b border-border">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline" className="font-mono">
                      week ending {issue.week_ending}
                    </Badge>
                    vs {issue.compared_to} · generated {issue.generated_at?.slice(0, 16)}Z
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyForLLM}
                            data-testid="digest-copy-llm-btn">
                      {copied ? <Check className="w-3.5 h-3.5 mr-1 text-emerald-400" />
                              : <Copy className="w-3.5 h-3.5 mr-1" />}
                      Copy for LLM
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadMd}
                            data-testid="digest-download-btn">
                      <Download className="w-3.5 h-3.5 mr-1" /> .md
                    </Button>
                  </div>
                </div>
                <article className="prose prose-invert prose-sm max-w-none
                  prose-headings:font-semibold prose-table:text-xs
                  prose-th:border prose-th:border-border prose-th:px-2 prose-th:py-1
                  prose-td:border prose-td:border-border prose-td:px-2 prose-td:py-1">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{issue.markdown}</ReactMarkdown>
                </article>
              </div>
            )}
            {!loadingIssue && !issue && (
              <div className="py-16 text-center text-muted-foreground">
                <Newspaper className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select an issue, or publish this week's.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
